import {
  Imagery,
  Integrations,
  Layers,
  Notes,
  PermissionModel,
  Roles,
} from "./modelTypes";

export const Action = {
  create: "create",
  delete: "delete",
  update: "update",
  view: "view",
};

export type Role = "administrator" | "advisor" | "editor" | "viewer";

export type UserActions = keyof typeof Action;

export type OperationV2 = {
  user_permission: {
    "enrollments-svc"?: Record<string, Array<string>>;
    "roles-svc"?: Record<string, Array<string>>;
    nitrogen_permission?: Record<string, Array<string>>;
    "inputs-svc"?: Record<string, Array<string>>;
    "activities-svc"?: Record<string, Array<string>>;
    "layers-store"?: Record<string, Array<string>>;
    financial_permission?: Record<string, Array<string>>;
    "fields-svc"?: Record<string, Array<string>>;
    landvisor?: Record<string, Array<string>>;
  };
  user_role?: {
    base_role: Role;
  };
};

export type Override = {
  allow: Array<string>;
  deny: Array<string>;
  application: string;
};

export type OperationV4 = {
  permissions: {
    allow: Array<string>;
    deny: Array<string>;
    overrides?: Array<Override>;
  };
};

const modelNamespaceApplicationMap = {
  roles: "roles-svc",
  fields: "fields-svc",
  enrollments: "enrollments-svc",
  activities: "fields-svc",
  analysis: "fields-svc",
  carbon: "fields-svc",
  documents: "fields-svc",
  inputs: "fields-svc",
  integrations: "fields-svc",
  imagery: "fields-svc",
  layers: "layers-store",
  notes: "roles-svc",
  landvisor: "fields-svc",
};

// TODO: Remove this edge case once we have the new v4 permissions
/*
 * These are edge cases where the user should not be able to perform the
 * action on the model. This is a temporary solution until we can get the userCan
 * hook using the new v4 permissions.
 */
const edgeCases = [
  // Layers Store
  {
    role: ["viewer"],
    action: ["create", "update", "delete"],
    model: Layers.layer,
  },
  // Notes
  {
    role: ["editor", "advisor"],
    action: ["delete"],
    model: Notes.note,
  },
  // Imagery
  {
    role: ["administrator", "editor", "viewer"],
    action: ["create", "update", "delete"],
    model: Imagery.sentinel2_enrollment,
  },
  // Integrations
  {
    role: ["editor", "viewer"],
    action: ["create", "update", "delete"],
    model: Integrations.connection,
  },
  // Roles
  {
    role: ["advisor", "editor", "viewer"],
    action: ["update"],
    model: Roles.corteva_data_management,
  },
];

/**
 * This function expands a permission key into the list of all possible
 * permission that would satisfy a user to take that action on that model.
 *
 * Example: if passed in "fields", "field_group", and "view" as a the
 * parameters then this function would return:
 *
 * [ 'fields:field_group:view', 'fields:*:view', '*:field_group:view',
 * '*:field_group:*', 'fields:*:*', 'fields:field_group:*', '*:*:view', '*:*:*']
 *
 * @param applicationName
 * @param modelName
 * @param act
 * @returns a list of all possible permissions that would allow a user to take * the action a model
 */
const permutations = (
  applicationName: string,
  modelName: string,
  act: string
): string[] => {
  const appList = [applicationName, "*"];
  const modelList = [modelName, "*"];
  const actionList = [act, "*"];

  const permList: Array<string> = [];

  appList.forEach((app: string) =>
    modelList.forEach((model: string) =>
      actionList.forEach((action: string) =>
        permList.push(`${app}:${model}:${action}`)
      )
    )
  );

  return permList;
};

/**
 * Check user permission for v2 operation payload
 *
 * Returns a boolean indicating whether a user can perform a given action on a
 * given model. The model should be passed from the modelTypes file.
 *
 * example: userCanV2("create", Fields.field, operation object)
 *
 * @param action Action user wants to perform
 * @param model Use the constants from model_types
 * @param operation Operation object returned from roles-svc v2
 */
const userCanV2 = (
  action: UserActions,
  model: PermissionModel,
  operation: OperationV2
): boolean => {
  const { user_permission, user_role } = operation;
  const isPermEmtyOrFalsy =
    !user_permission || Object.keys(user_permission).length === 0;
  if (isPermEmtyOrFalsy) {
    return false;
  }

  const modelsToCheck = model.split("|");
  for (const m of modelsToCheck) {
    const modelParts = m.split(":");
    if (modelParts.length !== 3) {
      return false;
    }
    const application = modelNamespaceApplicationMap[modelParts[0]];
    if (operation.user_permission[application] === undefined) {
      return false;
    }

    // Edge cases where the user should not be able to perform the action on the model
    if (
      edgeCases.some(
        (edgeCase) =>
          edgeCase.role.includes(user_role?.base_role || "") &&
          edgeCase.action.includes(action) &&
          edgeCase.model === model
      )
    ) {
      return false;
    }

    const appModels = Object.keys(operation.user_permission[application]);
    let appActions: Array<string> = [];

    if (appModels.includes(model)) {
      appActions = operation.user_permission[application][modelParts[1]];
    } else if (appModels.includes("*")) {
      appActions = operation.user_permission[application]["*"];
    }
    const checkAction = modelParts[2] === "?" ? action : modelParts[2];
    if (!(appActions.includes(checkAction) || appActions.includes("*"))) {
      return false;
    }
  }
  return true;
};

/**
 * Check user permission for v4 operation payload
 *
 * Returns a boolean indicating whether a user can perform a given action on a
 * given model. The model should be passed from the modelTypes file.
 *
 * example: userCanV4("create", Fields.field, operation object)
 *
 * @param action Action user wants to perform
 * @param model Use the constants from model_types
 * @param operation Operation object returned from roles-svc v4
 */
const userCanV4 = (
  action: UserActions,
  model: PermissionModel,
  operation: OperationV4
): boolean => {
  const { permissions } = operation;
  if (!permissions) {
    return false;
  }

  const allModelsToCheck: string[][] = [];
  const modelsToCheck = model.split("|");
  for (const m of modelsToCheck) {
    const checkList: string[] = [];
    const modelParts = m.split(":");
    if (modelParts.length !== 3) {
      return false;
    }

    const [applicationName, modelName, modelAction] = modelParts;

    if (modelNamespaceApplicationMap[applicationName] === undefined) {
      return false;
    }

    let actionToCheck: string;
    if (modelAction === "?") {
      actionToCheck = action; // use the action passed in as an argument
    } else {
      actionToCheck = modelAction; // use the action in the permission key
    }
    checkList.push(...permutations(applicationName, modelName, actionToCheck));
    allModelsToCheck.push(checkList);
  }

  /**
   * Sometimes, we'll have models composed of sub-models and are split
   * by a pipe character `|`.
   *
   * Example: enrollments:field_enrollment:?|fields:field:view
   *
   * It reads as the "user must be able to View Fields to perform
   * actions on Field Enrollments".
   *
   * Before accessing, confirm BOTH model/action and sub-model/action are in
   * the user's permissions. Both permissions should exist. Otherwise, deny the
   * user access.
   *
   * @param userPermissionsList
   * @returns boolean
   */
  const modelActionFound = (userPermissionsList: Array<string>) => {
    return allModelsToCheck.every((checkList) =>
      checkList.find((perm) => {
        return userPermissionsList.includes(perm);
      })
    );
  };

  const { allow, deny, overrides } = permissions;
  const [applicationName] = model.split(":");

  if (overrides) {
    overrides.forEach(({ application, allow, deny }) => {
      if (application === applicationName) {
        return modelActionFound(allow) && !modelActionFound(deny);
      }
      return;
    });
  }
  return modelActionFound(allow) && !modelActionFound(deny);
};

const isOperationV2 = (
  operation: OperationV2 | OperationV4
): operation is OperationV2 => {
  return (operation as OperationV2).user_permission !== undefined;
};

/**
 * @param action Action user wants to perform
 * @param model Use the constants from model_types
 * @param operation Operation object returned from roles-svc v2 or v4
 */
const userCan = (
  action: UserActions,
  model: PermissionModel,
  operation: OperationV2 | OperationV4
) => {
  if (isOperationV2(operation)) {
    return userCanV2(action, model, operation);
  } else {
    return userCanV4(action, model, operation);
  }
};

export * from "./modelTypes";
export default userCan;
